import { useTranslation } from "react-i18next";

import type { StageSnapshot } from "@msc/shared";

import { groupHeaders } from "../../lib/customerView/deckGroups.js";

/**
 * Einstiegsfolie: die Themen des Termins in der Reihenfolge, in der sie
 * kommen. Nummeriert, damit der Kunde sich orientieren kann; ein Haken steht
 * an allem, was bereits besprochen ist — beim Zurückspringen auf die Agenda
 * ist damit ohne Worte klar, wo man steht.
 *
 * Gibt es aktivierte Agenda-Punkte (D9), stehen ihre Titel als eigener,
 * eigens nummerierter Block VOR den Tickets — mit eigener Überschrift, damit
 * klar bleibt, dass es sich um zwei verschiedene Dinge handelt. Die
 * Ticketnummerierung beginnt danach wieder bei 1: Die Zählung der Tickets ist
 * unabhängig von der Zahl der Agenda-Punkte, so wie sie es auch ohne Agenda
 * wäre.
 */
export function AgendaSlide({ snapshot }: { snapshot: StageSnapshot }) {
  const { t } = useTranslation();
  const agendaItems = snapshot.agendaItems;
  // Gruppen wie in der Ticketliste (#278) — als Zwischenzeilen, die
  // Nummerierung läuft durch. Ein älteres Cockpit schickt kein `group`, dann
  // gibt es keine Überschriften.
  const groupOf = new Map(snapshot.deck.map((d) => [d.key, d.group]));
  const headers = groupHeaders(
    snapshot.deck.map((d) => d.key),
    (k) => groupOf.get(k),
  );
  return (
    <div className="stage-slide">
      <h1 className="stage-title">{t("stage.agenda")}</h1>
      {agendaItems.length > 0 && (
        <>
          <div className="stage-label">{t("stage.agendaBlock")}</div>
          <ul className="stage-list">
            {agendaItems.map((item, index) => (
              <li key={item.kind}>
                <span className="stage-list-number">{index + 1}.</span>
                <span className="stage-list-title">{item.title}</span>
              </li>
            ))}
          </ul>
          <div className="stage-label">{t("stage.ticketsBlock")}</div>
        </>
      )}
      <ul className="stage-list">
        {snapshot.deck.flatMap((item, index) => {
          const header = headers.get(index);
          const row = (
            <li key={item.key}>
              <span className="stage-list-number">{index + 1}.</span>
              <span className="stage-list-title">{item.title}</span>
              {item.reviewed && <span className="stage-check">✓</span>}
            </li>
          );
          return header === undefined
            ? [row]
            : [
                <li key={`group:${index}`} className="stage-list-group">
                  {header}
                </li>,
                row,
              ];
        })}
      </ul>
    </div>
  );
}
